import { useContext, useEffect, useState } from 'react'

import type { Props as ArticleCardProps } from '@hhgtech/hhg-components/build/components/molecules/articleCard/_deprecated_index'
import { useSSOV2Store } from '@hhgtech/hhg-components/ssoV2'
import innertext from 'innertext'
import Link from 'next/link'

import { callApiNew } from 'api/axios'
import { PATHS } from 'api/paths'
import { getHelloSitesUrl } from 'api/utils/paths'
import { CardArticleAuthorName } from 'components/atoms/CardArticleV2/CardArticleAuthor'
import {
  NextImageWrap,
  Props as NextImageWrapProps,
} from 'components/atoms/imageWrap'
import LazyLoadWrapper from 'components/atoms/lazyLoadWrapper'
import { domainLocales } from 'domainLocales'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { useScreenSize } from 'hooks/useScreenSize'
import {
  DEFAULT_AVATAR_IMG,
  DEFAULT_IMG,
  IS_SSOV2_ENABLED,
  SSO_URL,
} from 'interfaces/constants'
import { Article, LOCALE } from 'interfaces/types'
import { AppContext } from 'state/appContext'
import { getAuthorFromArticleApi } from 'utils/author'
import { renderDate } from 'utils/renderDate'

import {
  StyledArticleCard,
  StyledAvatarReviewer,
  StyledItemImageContain,
  StyledTinyBadge,
} from './index.styled'
import { actionTypes } from '../../../state/actionTypes'

export type Props = Partial<ArticleCardProps> & {
  article: Article
  disabledBadge?: boolean
  className?: string
  isLazyLoad?: boolean
  hideBookmark?: boolean
  bannerImageWrapProps?: Partial<NextImageWrapProps>
  authorImageWrapProps?: Partial<NextImageWrapProps>
  headingAs?: string
} & {
  dataEventCategoryForReviewer?: string
  dataEventActionForReviewer?: string
  dataEventLabelForReviewer?: string
}

export const ArticleCardWrapper = ({
  article,
  avatarImg,
  authorName,
  authorLink,
  title,
  text,
  disabledBadge = true,
  className,
  uploadTime,
  isLazyLoad,
  bannerImageWrapProps,
  authorImageWrapProps,
  banner,
  hideBookmark,
  dataEventCategoryForReviewer,
  dataEventActionForReviewer,
  ...rest
}: Props) => {
  const {
    state: { userInfo },
    dispatch,
  } = useContext(AppContext)
  const isMobile = useIsMobile()
  const { isLargeMobile } = useScreenSize()
  const { bookmarks = [] } = userInfo || {}
  const [isBookmarked, setIsBookmarked] = useState(
    bookmarks.indexOf(article.id) !== -1,
  )
  const [isBookmarkDisplayed, setIsBookmarkDisplayed] = useState(false)
  const { locale = LOCALE.PhilippinesEnglish } = useRouterExtend()
  const authorV2 = getAuthorFromArticleApi({
    article,
    locale,
    version: 'short',
  })
  const bookmarkArticle = (id: string | number) => {
    if (userInfo?.id) {
      setIsBookmarked((_isBookmarked) => !_isBookmarked)
      if (isBookmarked) {
        ;(async () => {
          const { _status } = await callApiNew(
            PATHS.SSO.DELETE_BOOKMARK,
            {
              method: 'DELETE',
              data: {
                host_name: domainLocales[locale],
                post_ids: [id],
              },
            },
            'sso',
          )
          if (_status === 1) {
            dispatch({
              type: actionTypes.REMOVE_BOOKMARKS,
              payload: {
                bookmarks: [id],
                bookmarkId: id,
              },
            })
          }
        })()
      } else {
        ;(async () => {
          const { _status, _data } = await callApiNew(
            PATHS.SSO.CREATE_BOOKMARK,
            {
              method: 'POST',
              data: {
                host_name: domainLocales[locale],
                post_ids: [id],
              },
            },
            'sso',
          )
          if (_status === 1) {
            dispatch({
              type: actionTypes.ADD_BOOKMARKS,
              payload: {
                bookmarks: [id],
                bookmarkId: id,
                isFirstTimeBookmark: _data.first_time_bookmark,
              },
            })
          }
        })()
      }
    } else {
      const currentLocation = window.location.href
      const returnUrlEncode = encodeURIComponent(
        `${currentLocation}${
          currentLocation.includes('?') ? '&' : '?'
        }source=bookmark,${id}`,
      )
      if (IS_SSOV2_ENABLED) {
        useSSOV2Store.getState().triggerLogin({
          source: `bookmark,${id}`,
        })
      } else {
        window.location.href = SSO_URL
          ? `${SSO_URL}?returnUrl=${returnUrlEncode}`
          : currentLocation
      }
    }
  }

  useEffect(() => {
    setIsBookmarkDisplayed(!hideBookmark)
  }, [])

  useEffect(() => {
    setIsBookmarked(bookmarks.indexOf(article.id) !== -1)
  }, [bookmarks.length])

  const AvatarComp = !!authorV2.avatar_permalink ? Link : 'span'

  const authorUrlDomain = authorV2.avatar_permalink
    ? getHelloSitesUrl(locale, true) + authorV2.avatar_permalink
    : ''

  const ArticleCard = (
    <StyledArticleCard
      isPremium={locale !== LOCALE.Vietnam && article.isPremium}
      isMobile={isMobile}
      className={className}
      dataEventCategoryForBookmark="Bookmark"
      dataEventActionForBookmark="Click"
      dataEventLabelForBookmark={`https://${domainLocales[locale]}${article.permalinkDecode}`}
      dataEventLabelForTitle={`https://${domainLocales[locale]}${article.permalinkDecode}`}
      dataEventLabelForCategory={`https://${domainLocales[locale]}${article.category.permalinkDecode}`}
      dataEventLabelForBanner={`https://${domainLocales[locale]}${article.permalinkDecode}`}
      hasAffiliate={article.hasAffiliate}
      articleLink={article.permalink}
      categoryLink={article.category.permalink || '#'}
      categoryName={
        article.category?.displayName ||
        article.category?.name ||
        article.categories?.[0]?.displayName ||
        article.categories?.[0]?.name ||
        ''
      }
      banner={
        banner || (
          <NextImageWrap
            src={
              (isMobile ? article.featureImageMobile : article.featureImage) ||
              article.featureImage ||
              DEFAULT_IMG
            }
            fill
            alt={article.postName}
            {...bannerImageWrapProps}
          />
        )
      }
      specialty={article.expert?.specialty || article.author?.specialty}
      {...rest}
      uploadTime={
        renderDate(uploadTime || article.modifiedTime, locale) ||
        uploadTime ||
        article.modifiedTime
      }
      title={
        innertext(title || article.postTitle || article.postName || '') || ''
      }
      text={
        typeof text !== 'undefined'
          ? innertext(text || '')
          : article.excerpt || article.description || article.postContent
          ? innertext(
              article.excerpt ||
                article.description ||
                article.postContent?.split('\r\n')[0] ||
                '',
            )
          : ''
      }
      avatarImg={
        <AvatarComp
          href={authorV2.avatar_permalink}
          className="pointer-event-child-none"
          data-event-category={dataEventCategoryForReviewer}
          data-event-action={dataEventActionForReviewer}
          data-event-label={authorUrlDomain}
          prefetch={false}
        >
          <StyledAvatarReviewer>
            <StyledItemImageContain>
              <NextImageWrap
                className="avatar"
                src={authorV2.avatar || DEFAULT_AVATAR_IMG}
                alt={authorV2.avatar_alt}
                {...(rest.size === 'lg' && !isMobile && !isLargeMobile
                  ? {
                      width: 32,
                      height: 32,
                    }
                  : {
                      width: 24,
                      height: 24,
                    })}
                placeholder={undefined}
                blurDataURL={undefined}
                {...authorImageWrapProps}
              />
            </StyledItemImageContain>
            {!disabledBadge && authorV2.indicator && (
              <StyledTinyBadge className="badge">
                <img
                  src={authorV2.indicator}
                  loading="lazy"
                  alt="badge"
                  width={12}
                  height={12}
                />
              </StyledTinyBadge>
            )}
          </StyledAvatarReviewer>
        </AvatarComp>
      }
      authorName={
        <span
          data-event-category={dataEventCategoryForReviewer}
          data-event-action={dataEventActionForReviewer}
          data-event-label={authorUrlDomain}
          className="pointer-event-child-none"
        >
          <CardArticleAuthorName
            {...authorV2}
            formatOptionsProps={{
              expert_display_name: {
                href: '', // TODO : Have to remove
              },
            }}
          />
          &nbsp;
        </span>
      }
      authorLink={authorV2.avatar_permalink}
      isBookmarkDisplayed={isBookmarkDisplayed}
      isBookmark={isBookmarked}
      onBookmark={() => bookmarkArticle(article.id)}
      styleLabel={{
        color: article.category?.textColor || '#008C50',
        ...rest.styleLabel,
      }}
    />
  )

  if (isLazyLoad)
    return (
      <LazyLoadWrapper
        placeholder={
          <div
            style={{
              opacity: 0,
              pointerEvents: 'none',
              height: 180,
            }}
          >
            <a href={authorV2.avatar_permalink}>{authorV2.avatar_permalink}</a>
            <a href={authorV2.avatar_permalink}>
              <img data-src={authorV2.avatar} alt={authorV2.avatar_alt} />
            </a>
            <div>
              <CardArticleAuthorName
                {...authorV2}
                formatOptionsProps={{
                  expert_display_name: {
                    'data-event-category': dataEventCategoryForReviewer,
                    'data-event-action': dataEventActionForReviewer,
                    'data-event-label': authorUrlDomain,
                  },
                }}
              />
            </div>
            <a href={rest.articleLink}>
              <img data-src={article.featureImage} alt={article.postTitle} />
              <img
                data-src={article.featureImageMobile}
                alt={article.postTitle}
              />
              <img
                data-src={article.featureImageMobileXs}
                alt={article.postTitle}
              />
              <div>{rest.categoryName}</div>
              <div>{uploadTime}</div>
              <div>{title || ''}</div>
              <div>{text || ''}</div>
            </a>
          </div>
        }
      >
        {ArticleCard}
      </LazyLoadWrapper>
    )

  return ArticleCard
}
